import { usePerms } from 'contexts/PermsContext';
import { selectSelectedDeviceId } from 'features/Integrations/state/selectors/deviceSelectors';
import { openStageSettings } from 'features/Integrations/state/slices/deviceSlice';
import { useMemo } from 'react';
import { Alert, Button, Container } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import CopySettingsBar from './components/CopySettingsBar/CopySettingsBar';
import FloodDeviceSettingsForm from './features/FloodDeviceSettingsForm/FloodDeviceSettingsForm';
import FloodSimSettings from './features/FloodSimSettings/FloodSimSettings';
import useSettingsDraft from './hooks/useSettingsDraft';
import useSettingsSave from './hooks/useSettingsSave';
import { buildSavePlan, getChangedSettings } from './utils/settings.utils';

const FloodDeviceSettings = ({ mobile = false }) => {
  const perms = usePerms();
  const dispatch = useDispatch();
  const { data: connectedDevices = [] } = useGetConnectedDevicesQuery();
  const selectedDeviceId = useSelector(selectSelectedDeviceId);

  const device = connectedDevices.find((device) => device.id === selectedDeviceId) || null;
  const {
    deviceSettings,
    setDeviceSettings,
    handleInputChange,
    pendingDownstream,
    setPendingDownstream,
    stageDownstream,
    unstageDownstream,
  } = useSettingsDraft(device);
  const changedSettings = useMemo(
    () => getChangedSettings(device?.device_settings, deviceSettings),
    [device?.device_settings, deviceSettings],
  );
  const hasChanges = Object.keys(changedSettings).length > 0 || pendingDownstream.length > 0;
  const savePlan = useMemo(
    () =>
      buildSavePlan({
        device,
        deviceSettings,
        changedSettings,
        pendingDownstream,
        connectedDevices,
      }),
    [device, deviceSettings, changedSettings, pendingDownstream, connectedDevices],
  );
  const { saveSettings, commandSent } = useSettingsSave({ savePlan, setPendingDownstream });

  const openDevice = (deviceId) => {
    if (hasChanges) return;
    dispatch(openStageSettings(deviceId));
  };

  const canEditFloodStage = perms.testPermission(
    [8, 9, 10, 11, 12, 13],
    ['Create-EditFloodStageConnections'],
  );

  // The simulator settings are behind their own lock (Mike, 2026-08-28): a
  // maintenance operator (or super admin) AND ManageFloodSimulation on the
  // role — no admin bypass on the key. The backend refuses the same way.
  const canEditSim =
    perms.testPermission([8, 12, 13], ['ManageFloodSimulation']) && perms.isMaintenanceOperator();
  if (mobile) {
    return (
      <Alert variant="warning" className="m-3">
        This feature is currently not implemented. Please manage Flood Stage devices from the
        desktop website.
      </Alert>
    );
  }

  return (
    <Container fluid className="py-3 text-white">
      <div className="d-flex flex-column gap-3 min-h-75vh">
        {/* Copy settings from another device */}
        <CopySettingsBar
          connectedDevices={connectedDevices}
          setDeviceSettings={setDeviceSettings}
          enabled={canEditFloodStage}
        />
        {device && (
          // Main device settings form, holds all the general Settings
          <FloodDeviceSettingsForm
            deviceSettings={deviceSettings}
            setDeviceSettings={setDeviceSettings}
            handleInputChange={handleInputChange}
            device={device}
            enabled={canEditFloodStage}
          />
        )}
        {device && canEditSim && (
          // Advanced settings for Flood SIM configuration, only shown if user has SIM edit permissions
          <FloodSimSettings
            sim={deviceSettings.sim}
            device={device}
            onChange={(newSim) => setDeviceSettings((prev) => ({ ...prev, sim: newSim }))}
            enabled={canEditSim}
            hasUnsavedChanges={hasChanges}
            onOpenDevice={openDevice}
            pendingDownstream={pendingDownstream}
            onStageDownstream={stageDownstream}
            onUnstageDownstream={unstageDownstream}
          />
        )}
        {canEditFloodStage && (
          <div className="small border border-secondary rounded px-3 py-2">
            <div className="fw-semibold">
              {savePlan.summary.length ? 'Save will:' : 'Nothing to save — no changes yet.'}
            </div>
            {savePlan.summary.map((line) => (
              <div key={line.device}>
                <span className="text-warning fw-semibold">{line.device}</span>
                <span className="text-secondary"> — {line.what}</span>
              </div>
            ))}
          </div>
        )}
        {canEditFloodStage && (
          <div className="d-flex justify-content-end align-items-center gap-3">
            {commandSent && (
              <span className="text-success fw-semibold">Command sent to server!</span>
            )}
            <Button
              size="lg"
              onClick={saveSettings}
              disabled={!hasChanges}
              variant={hasChanges ? 'primary' : 'secondary'}
              className="aware-save-button"
            >
              Save
            </Button>
          </div>
        )}
      </div>
    </Container>
  );
};

export default FloodDeviceSettings;
